<template>
  <div class="page-container">
    <!-- 筛选区域 -->
    <Filter :params="params" @search="searchData">
      <template #handle>
        <el-form-item>
          <el-button icon="el-icon-plus" type="danger" @click="handleAdd">
            {{ $t('smartRouting.person.addPerson') }}
          </el-button>
        </el-form-item>
      </template>
    </Filter>

    <!-- 表格区域 -->
    <Table
      ref="tableRef"
      :tableData="tableDataList"
      :total="data.total"
      @update="updatePage"
      @sort-change="handleSortChange"
    >
      <el-table-column prop="code" :label="$t('smartRouting.person.personId')" width="120" />

      <el-table-column :label="$t('smartRouting.person.avatar')" width="80">
        <template #default="{ row }">
          <el-avatar :size="32" :src="row.avatarUrl" shape="square">
            <span class="avatar-text">{{ $t('smartRouting.person.avatar').charAt(0) }}</span>
          </el-avatar>
        </template>
      </el-table-column>

      <el-table-column prop="name" :label="$t('smartRouting.person.name')" width="120" />

      <el-table-column prop="companyName" :label="$t('smartRouting.person.companyName')" min-width="160" />

      <!-- 分组：枚举 -> 中文 -->
      <el-table-column prop="groupType" :label="$t('smartRouting.person.group')" width="100">
        <template #default="{ row }">
          <span>{{ groupTypeLabel(row.groupType) }}</span>
        </template>
      </el-table-column>

      <el-table-column prop="createBy" :label="$t('smartRouting.person.createBy')" width="120" />
      <el-table-column prop="createTime" :label="$t('smartRouting.person.createTime')" width="160" />
      <el-table-column prop="updateBy" :label="$t('smartRouting.person.updateBy')" width="120" />
      <el-table-column prop="updateTime" :label="$t('smartRouting.person.updateTime')" width="160" />

      <el-table-column :label="$t('smartRouting.person.action')" fixed="right" width="120">
        <template #default="{ row }">
          <el-tooltip :content="$t('smartRouting.person.view')" placement="top">
            <el-button type="text" size="small" icon="el-icon-view" @click="handleView(row)" />
          </el-tooltip>
          <el-tooltip :content="$t('smartRouting.person.edit')" placement="top">
            <el-button :disabled="row.source == 'DING'" type="text" size="small" icon="el-icon-edit" @click="handleEdit(row)" />
          </el-tooltip>
          <el-tooltip :content="$t('smartRouting.person.delete')" placement="top">
            <el-button :disabled="row.source == 'DING'" type="text" style="color: red;" size="small" icon="el-icon-delete" @click="handleDelete(row)" />
          </el-tooltip>
        </template>
      </el-table-column>
    </Table>

    <!-- 新增/编辑/查看弹窗 -->
    <el-dialog
      :title="dialogMode === 'view' ? $t('smartRouting.person.personDetail') : dialogTitle"
      v-model="data.dialogVisible"
      destroy-on-close
      :modal="true"
      :append-to-body="true"
      width="820px"
      class="person-dialog"
    >
      <!-- ==================== 查看模式 ==================== -->
      <div v-if="dialogMode === 'view'" class="view-container">
        <div class="view-header">
          <!-- 查看态：右上角展示放大图标 -->
          <div class="view-avatar-wrapper" @click="previewImage(formData.avatarUrl)">
            <el-avatar :size="120" :src="formData.avatarUrl" shape="square">
              <span class="avatar-text" style="font-size: 24px;">{{ $t('smartRouting.person.avatar').charAt(0) }}</span>
            </el-avatar>
            <!-- 右上角放大图标 -->
            <div class="zoom-icon-top" v-if="formData.avatarUrl">
              <i class="el-icon-full-screen"></i>
            </div>
          </div>
          
          <div class="view-header-info">
            <div class="name-row">
              <span class="name-text">{{ formData.name }}</span>
              <el-tag v-if="formData.groupType === 'VIP'" type="warning" effect="light" round size="small">{{ $t('smartRouting.person.groupVip') }}</el-tag>
            </div>
            <div class="id-text">{{ formData.code || formData.personId || '—' }}</div>
          </div>
        </div>

        <div class="view-details">
          <el-row :gutter="40" class="detail-row">
            <el-col :span="12" class="detail-item">
              <span class="label">{{ $t('smartRouting.person.gender') }}</span>
              <span class="value">{{ genderLabel(formData.gender) }}</span>
            </el-col>
            <el-col :span="12" class="detail-item">
              <span class="label">{{ $t('smartRouting.person.companyName') }}</span>
              <span class="value">{{ formData.companyName || '—' }}</span>
            </el-col>
          </el-row>

          <el-row :gutter="40" class="detail-row">
            <el-col :span="12" class="detail-item">
              <span class="label">{{ $t('smartRouting.person.position') }}</span>
              <span class="value">{{ formData.position || '—' }}</span>
            </el-col>
            <el-col :span="12" class="detail-item"></el-col>
          </el-row>

          <el-row :gutter="40" class="detail-row">
            <el-col :span="12" class="detail-item">
              <span class="label">{{ $t('smartRouting.person.visitStartDate') }}</span>
              <span class="value">{{ formData.visitStartDate || '—' }}</span>
            </el-col>
            <el-col :span="12" class="detail-item">
              <span class="label">{{ $t('smartRouting.person.visitEndDate') }}</span>
              <span class="value">{{ formData.visitEndDate || '—' }}</span>
            </el-col>
          </el-row>

          <el-row :gutter="40" class="detail-row">
            <el-col :span="12" class="detail-item">
              <span class="label">{{ $t('smartRouting.person.createBy') }}</span>
              <span class="value">{{ viewMetaData.createBy || '—' }}</span>
            </el-col>
            <el-col :span="12" class="detail-item">
              <span class="label">{{ $t('smartRouting.person.createTime') }}</span>
              <span class="value">{{ viewMetaData.createTime || '—' }}</span>
            </el-col>
          </el-row>

          <el-row :gutter="40" class="detail-row">
            <el-col :span="12" class="detail-item">
              <span class="label">{{ $t('smartRouting.person.updateBy') }}</span>
              <span class="value">{{ viewMetaData.updateBy || '—' }}</span>
            </el-col>
            <el-col :span="12" class="detail-item">
              <span class="label">{{ $t('smartRouting.person.updateTime') }}</span>
              <span class="value">{{ viewMetaData.updateTime || '—' }}</span>
            </el-col>
          </el-row>
        </div>
      </div>

      <!-- ==================== 新增/编辑模式 ==================== -->
      <el-form
        v-else
        :model="formData"
        label-width="120px"
        ref="formRef"
        class="person-form"
      >
        <el-form-item :label="$t('smartRouting.person.personPhoto')" required prop="avatarUrl">
          <div class="avatar-upload-container">
            <div class="avatar-uploader" @click="triggerAvatarUpload" v-if="!formData.avatarUrl">
              <div class="avatar-placeholder">+</div>
            </div>
            
            <!-- 已上传头像：统一样式，右上角放大图标，底部悬浮操作按钮 -->
            <div class="avatar-preview" v-else>
              <img :src="formData.avatarUrl" class="avatar-image" @click="previewImage(formData.avatarUrl)" />
              
              <!-- 右上角放大图标 -->
              <div class="zoom-icon-top" @click.stop="previewImage(formData.avatarUrl)">
                <i class="el-icon-full-screen"></i>
              </div>

              <!-- 底部操作黑条 -->
              <div class="avatar-actions-bar">
                <span class="action-btn" @click.stop="triggerAvatarUpload">{{ $t('smartRouting.person.change') }}</span>
                <span class="action-btn delete-btn" @click.stop="removeAvatar">{{ $t('smartRouting.person.delete') }}</span>
              </div>
            </div>

            <div class="avatar-tips">
              <p>{{ $t('smartRouting.person.avatarUploadTip1') }}</p>
              <p class="warning-text">{{ $t('smartRouting.person.avatarUploadTip2') }}</p>
            </div>
            <input
              ref="avatarInputRef"
              type="file"
              accept="image/jpeg,image/png"
              style="display: none"
              @change="handleAvatarChange"
            />
          </div>
        </el-form-item>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('smartRouting.person.name')" required>
              <el-input v-model="formData.name" :placeholder="$t('smartRouting.person.namePlaceholder')" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('smartRouting.person.gender')">
              <el-select v-model="formData.gender" :placeholder="$t('smartRouting.person.pleaseSelect')" style="width: 100%;">
                <el-option :label="$t('smartRouting.person.genderMale')" value="MALE" />
                <el-option :label="$t('smartRouting.person.genderFemale')" value="FEMALE" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('smartRouting.person.companyName')">
              <el-input v-model="formData.companyName" :placeholder="$t('smartRouting.person.companyPlaceholder')" />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('smartRouting.person.group')">
              <el-select v-model="formData.groupType" :placeholder="$t('smartRouting.person.pleaseSelect')" style="width: 100%;">
                <el-option :label="$t('smartRouting.person.groupNormal')" value="NORMAL" />
                <el-option :label="$t('smartRouting.person.groupVip')" value="VIP" />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('smartRouting.person.position')">
              <el-input v-model="formData.position" :placeholder="$t('smartRouting.person.positionPlaceholder')" />
            </el-form-item>
          </el-col>
        </el-row>

        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item :label="$t('smartRouting.person.visitStartDate')" required>
              <el-date-picker
                v-model="formData.visitStartDate"
                type="date"
                :placeholder="$t('smartRouting.person.datePlaceholder')"
                format="DD/MM/YYYY"
                value-format="YYYY-MM-DD"
                style="width: 100%;"
              />
            </el-form-item>
          </el-col>
          <el-col :span="12">
            <el-form-item :label="$t('smartRouting.person.visitEndDate')" required>
              <el-date-picker
                v-model="formData.visitEndDate"
                type="date"
                :placeholder="$t('smartRouting.person.datePlaceholder')"
                format="DD/MM/YYYY"
                value-format="YYYY-MM-DD"
                style="width: 100%;"
              />
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>

      <template #footer>
        <template v-if="dialogMode === 'view'">
          <el-button size="mini" @click="data.dialogVisible = false">{{ $t('smartRouting.person.close') }}</el-button>
          <el-button size="mini" @click="switchToEdit">{{ $t('smartRouting.person.modify') }}</el-button>
          <el-button type="danger" size="mini" @click="handleDeleteCurrent">{{ $t('smartRouting.person.delete') }}</el-button>
        </template>
        <template v-else>
          <el-button size="mini" @click="data.dialogVisible = false">{{ $t('smartRouting.person.cancel') }}</el-button>
          <el-button type="danger" size="mini" :loading="submitting" @click="handleSubmit">
            {{ dialogMode === 'edit' ? $t('smartRouting.person.save') : $t('smartRouting.person.confirm') }}
          </el-button>
        </template>
      </template>
    </el-dialog>

    <!-- 图片预览全屏浮层 -->
    <div v-if="previewVisible" class="image-preview-mask" @click="closePreview">
      <img :src="previewUrl" class="preview-image" @click.stop />
    </div>
  </div>
</template>

<script>
import { ElMessage, ElMessageBox } from "element-plus";
import Table from "@/components/table/index";
import Filter from "@/components/table/filter";
import { ref, reactive } from "vue";
import { useI18n } from "vue-i18n";
import dayjs from "dayjs";
import { ossUpload } from "@/utils/oss";

import {
  reqPersonPage,
  reqPersonDetail,
  reqPersonCreate,
  reqPersonUpdate,
  reqPersonDelete,
} from "@/api/smartRouting/person";

export default {
  name: 'PersonLibrary',
  components: {
    Table,
    Filter,
  },
  setup() {
    const { t } = useI18n();
    const state = reactive({
      tableRef: null,
      sortData: {},
    });

    // ===== 放大预览状态 =====
    const previewVisible = ref(false);
    const previewUrl = ref('');

    const previewImage = (url) => {
      if (!url) return;
      previewUrl.value = url;
      previewVisible.value = true;
    };
    
    const closePreview = () => {
      previewVisible.value = false;
      previewUrl.value = '';
    };

    // ===== 枚举映射 =====
    const GENDER_MAP = {
      MALE: 'smartRouting.person.genderMale',
      FEMALE: 'smartRouting.person.genderFemale',
    };
    const GROUP_TYPE_MAP = {
      NORMAL: 'smartRouting.person.groupNormal',
      VIP: 'smartRouting.person.groupVip',
    };

    const genderLabel = (val) => {
      if (!val) return '—';
      if (val === 'MALE' || val === 'FEMALE') return t(GENDER_MAP[val]);
      return val;
    };
    
    const groupTypeLabel = (val) => {
      if (!val) return '—';
      if (val === 'NORMAL' || val === 'VIP') return t(GROUP_TYPE_MAP[val]);
      return val;
    };

    const toGenderEnum = (val) => {
      if (!val) return 'MALE';
      if (val === 'MALE' || val === 'FEMALE') return val;
      return 'MALE';
    };
    const toGroupTypeEnum = (val) => {
      if (!val) return 'NORMAL';
      if (val === 'NORMAL' || val === 'VIP') return val;
      return 'NORMAL';
    };

    // ===== 筛选参数配置 =====
    const params = [
      {
        key: 'keyword',
        value: '',
        label: t('smartRouting.person.name'),
        placeholder: t('smartRouting.person.name'),
      },
      {
        key: 'companyName',
        value: '',
        label: t('smartRouting.person.companyName'),
        placeholder: t('smartRouting.person.companyName'),
      },
    ];

    // ===== 数据状态 =====
    const data = reactive({
      page: 1,
      size: 10,
      total: 0,
      dialogVisible: false,
    });

    const tableDataList = ref([]);
    const formRef = ref(null);
    const avatarInputRef = ref(null);
    const dialogMode = ref('add');
    const dialogTitle = ref('');
    const editId = ref(null);
    const editCode = ref(null);
    const editPersonId = ref(null);
    const query = reactive({});
    const submitting = ref(false);

    const formData = reactive({
      code: '',
      personId: '',
      avatarUrl: '',
      avatarName: '',
      avatarPath: '',
      name: '',
      gender: 'MALE',
      companyName: '',
      groupType: 'NORMAL',
      position: '',
      visitStartDate: '',
      visitEndDate: '',
      createBy: '',
      createTime: '',
      updateBy: '',
      updateTime: '',
    });

    const viewMetaData = reactive({
      createBy: '',
      createTime: '',
      updateBy: '',
      updateTime: ''
    });

    // ===== 列表 =====
    const getData = async () => {
      try {
        const res = await reqPersonPage({
          ...query,
          ...state.sortData,
          page: data.page,
          size: data.size,
        });
        tableDataList.value =
          res?.records || res?.list || res?.data?.records || [];
        data.total = res?.total ?? res?.data?.total ?? 0;
      } catch (error) {
        console.error('getPersonListFailed:', error);
        tableDataList.value = [];
        data.total = 0;
        ElMessage.error(t('smartRouting.person.fetchDataFailed'));
      }
    };

    // ===== 搜索 / 分页 / 排序 =====
    const searchData = (searchParams) => {
      Object.assign(query, searchParams);
      data.page = 1;
      getData();
    };

    const updatePage = (page, size) => {
      data.page = page || 1;
      data.size = size || 10;
      getData();
    };

    const handleSortChange = (payload, orderMaybe) => {
      let prop, order;
      if (payload && typeof payload === 'object') {
        prop = payload.prop;
        order = payload.order;
      } else {
        prop = payload;
        order = orderMaybe;
      }
      const orderMap = { ascending: 'asc', descending: 'desc' };
      state.sortData = {
        sortField: prop || '',
        sort: orderMap[order] || '',
      };
      data.page = 1;
      getData();
    };

    // ===== 重置表单 =====
    const resetForm = () => {
      Object.assign(formData, {
        code: '',
        personId: '',
        avatarUrl: '',
        avatarName: '',
        avatarPath: '',
        name: '',
        gender: 'MALE',
        companyName: '',
        groupType: 'NORMAL',
        position: '',
        visitStartDate: '',
        visitEndDate: '',
        createBy: '',
        createTime: '',
        updateBy: '',
        updateTime: '',
      });
      editId.value = null;
      editCode.value = null;
      editPersonId.value = null;
      if (avatarInputRef.value) {
        avatarInputRef.value.value = '';
      }
    };

    // ===== 新增 =====
    const handleAdd = () => {
      dialogMode.value = 'add';
      dialogTitle.value = t('smartRouting.person.addPerson');
      resetForm();
      data.dialogVisible = true;
    };

    // ===== 编辑 =====
    const handleEdit = (row) => {
      dialogMode.value = 'edit';
      dialogTitle.value = t('smartRouting.person.editPerson');
      editId.value = row.id;
      editCode.value = row.code;
      editPersonId.value = row.personId;
      Object.assign(formData, {
        code: row.code || '',
        personId: row.personId || '',
        avatarUrl: row.avatarUrl || '',
        avatarName: row.avatarName || '',
        avatarPath: row.avatarPath || '',
        name: row.name || '',
        gender: toGenderEnum(row.gender),
        companyName: row.companyName || '',
        groupType: toGroupTypeEnum(row.groupType || row.group),
        position: row.position || '',
        visitStartDate: row.visitStartDate || '',
        visitEndDate: row.visitEndDate || '',
      });
      data.dialogVisible = true;
    };

    // ===== 查看 =====
    const handleView = async (row) => {
      dialogMode.value = 'view';
      dialogTitle.value = t('smartRouting.person.personDetail');
      editId.value = row.id;
      editCode.value = row.code;
      editPersonId.value = row.personId;

      Object.assign(formData, {
        code: row.code || '',
        personId: row.personId || '',
        avatarUrl: row.avatarUrl || '',
        name: row.name || '',
        gender: toGenderEnum(row.gender),
        companyName: row.companyName || '',
        groupType: toGroupTypeEnum(row.groupType || row.group),
        position: row.position || '',
        visitStartDate: row.visitStartDate || '',
        visitEndDate: row.visitEndDate || '',
      });
      Object.assign(viewMetaData, {
        createBy: row.createBy || '',
        createTime: row.createTime || '',
        updateBy: row.updateBy || '',
        updateTime: row.updateTime || '',
      });

      data.dialogVisible = true;

      try {
        const res = await reqPersonDetail(row.code);
        const detail = res?.data ?? res ?? {};
        Object.assign(formData, {
          code: detail.code || formData.code,
          personId: detail.personId || formData.personId,
          avatarUrl: detail.avatarUrl || formData.avatarUrl,
          name: detail.name || formData.name,
          gender: toGenderEnum(detail.gender || formData.gender),
          companyName: detail.companyName || formData.companyName,
          groupType: toGroupTypeEnum(detail.groupType || detail.group || formData.groupType),
          position: detail.position || formData.position,
          visitStartDate: detail.visitStartDate || formData.visitStartDate,
          visitEndDate: detail.visitEndDate || formData.visitEndDate,
        });
        Object.assign(viewMetaData, {
          createBy: detail.createBy || viewMetaData.createBy,
          createTime: detail.createTime || viewMetaData.createTime,
          updateBy: detail.updateBy || viewMetaData.updateBy,
          updateTime: detail.updateTime || viewMetaData.updateTime,
        });
      } catch (error) {
        console.error('getPersonDetailFailed:', error);
      }
    };

    const switchToEdit = () => {
      dialogMode.value = 'edit';
      dialogTitle.value = t('smartRouting.person.editPerson');
    };

    // ===== 头像上传 =====
    const triggerAvatarUpload = () => {
      if (dialogMode.value === 'view') return;
      avatarInputRef.value?.click();
    };

    const handleAvatarChange = async (e) => {
      const file = e.target.files?.[0];
      if (!file) return;

      if (!/\.(jpg|jpeg|png)$/i.test(file.name)) {
        ElMessage.warning(t('smartRouting.person.imageFormatTip'));
        e.target.value = '';
        return;
      }

      formData.avatarName = file.name;
      formData.avatarPath =
        'smart/person/' + dayjs().format('YYYY/MM/DD/') + file.name;

      try {
        const url = await ossUpload(file.name, file, {
          path: 'smart/person/' + dayjs().format('YYYY/MM/DD'),
          timestamp: false,
        });
        formData.avatarUrl = url;
        ElMessage.success(t('smartRouting.person.uploadSuccess'));
      } catch (error) {
        console.error('uploadFailed:', error);
        ElMessage.error(t('smartRouting.person.uploadFailed'));
        formData.avatarName = '';
        formData.avatarPath = '';
        formData.avatarUrl = '';
      } finally {
        e.target.value = '';
      }
    };

    const removeAvatar = () => {
      formData.avatarUrl = '';
      formData.avatarName = '';
      formData.avatarPath = '';
      if (avatarInputRef.value) {
        avatarInputRef.value.value = '';
      }
    };

    // ===== 提交 =====
    const handleSubmit = async () => {
      if (!formData.avatarUrl) {
        ElMessage.warning(t('smartRouting.person.photoTip'));
        return;
      }
      if (!formData.name) {
        ElMessage.warning(t('smartRouting.person.nameTip'));
        return;
      }
      if (!formData.visitStartDate) {
        ElMessage.warning(t('smartRouting.person.startDateTip'));
        return;
      }
      if (!formData.visitEndDate) {
        ElMessage.warning(t('smartRouting.person.endDateTip'));
        return;
      }

      const payload = {
        avatarUrl: formData.avatarUrl,
        avatarName: formData.avatarName,
        avatarPath: formData.avatarPath,
        name: formData.name,
        gender: formData.gender,
        companyName: formData.companyName || '',
        groupType: formData.groupType || 'NORMAL',
        position: formData.position || '',
        visitStartDate: formData.visitStartDate,
        visitEndDate: formData.visitEndDate,
      };

      submitting.value = true;
      try {
        if (dialogMode.value === 'edit') {
          await reqPersonUpdate({
            id: editId.value,
            code: editCode.value,
            personId: editPersonId.value,
            ...payload,
          });
          ElMessage.success(t('smartRouting.person.editSuccess'));
        } else {
          await reqPersonCreate(payload);
          ElMessage.success(t('smartRouting.person.addSuccess'));
        }
        data.dialogVisible = false;
        getData();
      } catch (error) {
        console.error('submitFailed:', error);
        ElMessage.error(dialogMode.value === 'edit' ? t('smartRouting.person.editFailed') : t('smartRouting.person.addFailed'));
      } finally {
        submitting.value = false;
      }
    };

    // ===== 列表外部删除 =====
    const handleDelete = async (row) => {
      try {
        await ElMessageBox.confirm(
          `${t('smartRouting.person.confirmDelete')} "${row.name}" ?`,
          t('smartRouting.person.deleteConfirmTitle'),
          {
            confirmButtonText: t('smartRouting.person.confirm'),
            cancelButtonText: t('smartRouting.person.cancel'),
            type: 'warning',
          }
        );
        await reqPersonDelete(row.code);
        ElMessage.success(t('smartRouting.person.deleteSuccess'));
        if (tableDataList.value.length === 1 && data.page > 1) {
          data.page -= 1;
        }
        getData();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('deleteFailed:', error);
          ElMessage.error(t('smartRouting.person.deleteFailed'));
        }
      }
    };

    // ===== 弹窗内部（查看态）删除 =====
    const handleDeleteCurrent = async () => {
      try {
        await ElMessageBox.confirm(
          `${t('smartRouting.person.confirmDelete')} "${formData.name}" ?`,
          t('smartRouting.person.deleteConfirmTitle'),
          {
            confirmButtonText: t('smartRouting.person.confirm'),
            cancelButtonText: t('smartRouting.person.cancel'),
            type: 'warning',
          }
        );
        await reqPersonDelete(editCode.value);
        ElMessage.success(t('smartRouting.person.deleteSuccess'));
        data.dialogVisible = false;
        if (tableDataList.value.length === 1 && data.page > 1) {
          data.page -= 1;
        }
        getData();
      } catch (error) {
        if (error !== 'cancel') {
          console.error('deleteFailed:', error);
          ElMessage.error(t('smartRouting.person.deleteFailed'));
        }
      }
    };

    // ===== 初始化加载 =====
    getData();

    return {
      tableRef: state.tableRef,
      params,
      data,
      tableDataList,
      formRef,
      avatarInputRef,
      dialogMode,
      dialogTitle,
      formData,
      viewMetaData,
      editId,
      editCode,
      editPersonId,
      submitting,
      genderLabel,
      groupTypeLabel,
      searchData,
      updatePage,
      handleSortChange,
      handleAdd,
      handleEdit,
      handleView,
      switchToEdit,
      handleSubmit,
      handleDelete,
      handleDeleteCurrent,
      triggerAvatarUpload,
      handleAvatarChange,
      removeAvatar,
      // 预览
      previewVisible,
      previewUrl,
      previewImage,
      closePreview,
    };
  },
};
</script>

<style lang="scss" scoped>
/* 样式部分完全保持不变 */
.page-container {
  height: 100%;
  padding: 50px 10px 0;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.avatar-text {
  font-size: 14px;
  color: #fff;
}

.person-form {
  padding-right: 10px;
  max-height: 65vh;
  overflow-y: auto;
  overflow-x: hidden;
}

.avatar-upload-container {
  display: flex;
  align-items: flex-start;
  gap: 16px;
}

.avatar-uploader {
  width: 120px;
  height: 120px;
  border: 1px dashed #dcdfe6;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #fafafa;
  cursor: pointer;
  transition: all 0.3s;
  flex-shrink: 0;
}

.avatar-uploader:hover {
  border-color: #409eff;
}

.avatar-placeholder {
  font-size: 30px;
  color: #c0c4cc;
  font-weight: 300;
}

/* === 预览容器 === */
.avatar-preview {
  position: relative;
  width: 120px;
  height: 120px;
  border-radius: 6px;
  overflow: hidden;
  flex-shrink: 0;
  border: 1px solid #dcdfe6;
  background-color: #f5f7fa;
}

.avatar-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  cursor: pointer;
}

/* 右上角放大图标（通用样式） */
.zoom-icon-top {
  position: absolute;
  top: 6px;
  right: 6px;
  width: 24px;
  height: 24px;
  background: rgba(0, 0, 0, 0.45);
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  font-size: 14px;
  cursor: pointer;
  transition: background 0.2s;
  z-index: 2;
  /* 增加极淡的阴影防止白色背景图看不清 */
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.2);
}

.zoom-icon-top:hover {
  background: rgba(0, 0, 0, 0.75);
}

/* === 编辑态底部操作黑条（调整了高度和排版） === */
.avatar-actions-bar {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: rgba(0, 0, 0, 0.65);
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 0; /* 适中高度 */
  opacity: 0;
  transform: translateY(100%);
  transition: all 0.3s ease;
  pointer-events: none;
  z-index: 2;
  backdrop-filter: blur(2px); /* 背景模糊增强质感 */
}

.avatar-preview:hover .avatar-actions-bar {
  opacity: 1;
  transform: translateY(0);
  pointer-events: auto;
}

.action-btn {
  color: #fff;
  font-size: 12px;
  cursor: pointer;
  padding: 0 14px;
  line-height: 1.2;
  letter-spacing: 1px;
  transition: color 0.2s;
}

.action-btn:not(:last-child) {
  border-right: 1px solid rgba(255, 255, 255, 0.3);
}

.action-btn:hover {
  color: #409eff;
}

/* 删除按钮默认红色 */
.delete-btn {
  color: #f56c6c !important;
}

.delete-btn:hover {
  color: #ff4d4f !important; /* 悬浮加深 */
}

/* === 查看态容器 === */
.view-avatar-wrapper {
  position: relative;
  margin-right: 24px;
  border-radius: 8px;
  overflow: hidden;
  background-color: #eef1f6;
  flex-shrink: 0;
  cursor: pointer;
}

.view-avatar-wrapper .el-avatar {
  display: block;
}

.avatar-tips {
  flex: 1;
  font-size: 12px;
  color: #909399;
  line-height: 1.6;
}

.avatar-tips p {
  margin: 0;
  margin-bottom: 4px;
}

.warning-text {
  color: #e6a23c;
}

.privacy-notice {
  display: flex;
  align-items: flex-start;
  background-color: #f4f4f5;
  padding: 12px 16px;
  border-radius: 4px;
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
  line-height: 1.5;
}

.notice-icon {
  margin-right: 8px;
  margin-top: 2px;
  font-size: 14px;
}

/* ==================== 图片预览层样式 ==================== */
.image-preview-mask {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.6);
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: zoom-out;
  animation: fadeIn 0.2s ease-in-out;
}

.preview-image {
  max-width: 80%;
  max-height: 80%;
  border-radius: 4px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.4);
  cursor: default;
  animation: zoomIn 0.2s ease-in-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes zoomIn {
  from { transform: scale(0.9); }
  to { transform: scale(1); }
}

/* ==================== 查看模式样式 ==================== */
.view-container {
  padding: 0 10px;
}

.view-header {
  display: flex;
  align-items: center;
  margin-bottom: 30px;
  margin-top: 10px;
}

.view-header-info {
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.name-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.name-text {
  font-size: 24px;
  font-weight: bold;
  color: #303133;
}

.id-text {
  font-size: 14px;
  color: #909399;
}

.view-details {
  width: 100%;
}

.detail-row {
  border-top: 1px solid #ebeef5;
  padding: 16px 0;
}

.detail-row:last-child {
  border-bottom: 1px solid #ebeef5;
}

.detail-item {
  display: flex;
  align-items: center;
  font-size: 14px;
}

.detail-item .label {
  color: #606266;
  width: 120px;
  flex-shrink: 0;
}

.detail-item .value {
  color: #303133;
  word-break: break-all;
}
</style>